import { useEffect } from "react";
import { useLocation, Link } from "react-router-dom";
import { Home, HelpCircle, ShieldAlert } from "lucide-react";
import { Button } from "@/components/ui/button";

const NotFound = () => {
  const location = useLocation();

  useEffect(() => {
    console.error(
      "404 Error: User attempted to access non-existent route:",
      location.pathname
    );
  }, [location.pathname]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-950 p-6 relative overflow-hidden">
      {/* Decorative cyber grid and lighting */}
      <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.01)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.01)_1px,transparent_1px)] bg-[size:32px_32px] pointer-events-none" />
      
      {/* Glowing neon background blobs */}
      <div className="absolute top-1/4 left-1/4 w-[450px] h-[450px] bg-blue-500/10 rounded-full blur-[120px] pointer-events-none animate-pulse-slow" />
      <div className="absolute bottom-1/4 right-1/4 w-[450px] h-[450px] bg-cyan-500/10 rounded-full blur-[120px] pointer-events-none animate-pulse-slow" style={{ animationDelay: "2s" }} />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-80 h-80 bg-violet-500/5 rounded-full blur-[100px] pointer-events-none animate-pulse-slow" style={{ animationDelay: "1s" }} />

      {/* Floating particles system (12 CSS-animated particles) */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        {Array.from({ length: 12 }).map((_, i) => (
          <div
            key={i}
            className="absolute rounded-full bg-cyan-400/25 blur-[1px] animate-float-particle"
            style={{
              width: `${Math.random() * 6 + 4}px`,
              height: `${Math.random() * 6 + 4}px`,
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 8}s`,
              animationDuration: `${Math.random() * 12 + 10}s`,
            }}
          />
        ))}
      </div>

      <div className="relative z-10 max-w-lg w-full text-center">
        {/* Floating cyber hazard tag */}
        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-red-500/25 bg-red-500/10 text-red-400 text-xs font-mono uppercase tracking-[0.15em] mb-6 shadow-[0_0_15px_rgba(239,68,68,0.1)]">
          <ShieldAlert className="h-4 w-4 animate-bounce" />
          Erro de Rota: Área Não Mapeada
        </div>

        {/* Glitching 404 Title */}
        <div className="relative inline-block mb-4">
          <h1 className="text-9xl font-black tracking-tighter text-transparent bg-clip-text bg-gradient-to-b from-white via-slate-200 to-slate-800 drop-shadow-[0_0_35px_rgba(59,130,246,0.3)] relative select-none animate-glitch-jitter">
            404
          </h1>
          <div className="absolute inset-0 text-9xl font-black tracking-tighter text-red-500/30 select-none pointer-events-none animate-glitch-layer-1" style={{ clipPath: "polygon(0 0, 100% 0, 100% 45%, 0 45%)" }}>404</div>
          <div className="absolute inset-0 text-9xl font-black tracking-tighter text-cyan-500/30 select-none pointer-events-none animate-glitch-layer-2" style={{ clipPath: "polygon(0 45%, 100% 45%, 100% 100%, 0 100%)" }}>404</div>
        </div>

        {/* Status text */}
        <h2 className="text-2xl font-bold mt-2 text-slate-100 tracking-tight">
          Cerca geográfica excedida!
        </h2>
        
        <p className="mt-3 text-slate-400 text-sm leading-relaxed max-w-sm mx-auto font-medium">
          A página <span className="font-mono bg-white/5 border border-white/10 px-2 py-0.5 rounded text-sm text-cyan-300 shadow-[0_0_10px_rgba(34,211,238,0.05)]">{location.pathname}</span> não existe no sistema itaCondo.
        </p>

        {/* Diagnostic logs panel */}
        <div className="mt-8 bg-black/60 rounded-2xl border border-white/10 p-5 text-left font-mono text-xs text-slate-400 space-y-1.5 shadow-2xl backdrop-blur-xl relative overflow-hidden">
          <div className="absolute top-0 right-0 w-24 h-24 bg-blue-500/5 rounded-full blur-xl pointer-events-none" />
          <p className="text-red-400/90">&gt; HTTP_STATUS_CODE: 404_NOT_FOUND</p>
          <p className="text-slate-500">&gt; STACK_TRACE: Router.resolve() failed to map path</p>
          <p className="text-slate-500">&gt; ATTEMPTED_URI: {location.pathname}</p>
          <p className="text-cyan-400/90">&gt; SUGGESTION: Redirecionar usuário para zona segura</p>
        </div>

        {/* Actions buttons */}
        <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center">
          <Button asChild size="lg" className="rounded-full h-12 bg-gradient-to-r from-blue-600 to-cyan-500 hover:from-blue-700 hover:to-cyan-600 text-white font-bold px-8 shadow-lg shadow-blue-500/20 hover:scale-105 transition duration-300">
            <Link to="/">
              <Home className="mr-2 h-4 w-4" />
              Retornar ao Início
            </Link>
          </Button>
          <Button asChild variant="outline" size="lg" className="rounded-full h-12 border-white/10 bg-white/5 text-white hover:bg-white/10 hover:border-white/20 font-bold px-8 hover:scale-105 shadow-md shadow-black/35 transition duration-300">
            <a href="https://web.whatsapp.com/send?phone=5521998231720" target="_blank" rel="noopener noreferrer">
              <HelpCircle className="mr-2 h-4 w-4 text-cyan-400" />
              Suporte Técnico
            </a>
          </Button>
        </div>
      </div>

      <style>{`
        @keyframes float-particle {
          0% { transform: translateY(0) translateX(0); opacity: 0; }
          10% { opacity: 0.8; }
          90% { opacity: 0.8; }
          100% { transform: translateY(-120px) translateX(30px); opacity: 0; }
        }
        .animate-float-particle {
          animation: float-particle infinite ease-in-out;
        }
        @keyframes pulse-slow {
          0%, 100% { opacity: 0.5; transform: scale(1); }
          50% { opacity: 0.8; transform: scale(1.1); }
        }
        .animate-pulse-slow {
          animation: pulse-slow 5s infinite ease-in-out;
        }
        
        /* Premium Glitch Keyframes */
        @keyframes glitch-layer-1 {
          0%, 100% { transform: translateX(0); }
          20% { transform: translateX(-3px) translateY(1px); }
          40% { transform: translateX(2px) translateY(-1px); }
          60% { transform: translateX(-2px) translateY(-2px); }
          80% { transform: translateX(3px) translateY(2px); }
        }
        @keyframes glitch-layer-2 {
          0%, 100% { transform: translateX(0); }
          20% { transform: translateX(3px) translateY(-1px); }
          40% { transform: translateX(-2px) translateY(2px); }
          60% { transform: translateX(2px) translateY(1px); }
          80% { transform: translateX(-3px) translateY(-2px); }
        }
        .animate-glitch-layer-1 {
          animation: glitch-layer-1 2s infinite ease-in-out;
        }
        .animate-glitch-layer-2 {
          animation: glitch-layer-2 2s infinite ease-in-out;
        }
      `}</style>
    </div>
  );
};

export default NotFound;
